import { useCallback, useEffect, useRef, useState } from "react"

// 🎤 Dictate for the situation note. SpeechRecognition only (Chrome/Safari
// webkit prefix; Chrome relays audio to Google — which is why the button is
// hidden entirely when unsupported or on an insecure origin, never a broken
// tap). Recognition must start from a user gesture; the hook exposes
// start/stop for the button and never auto-starts.

interface SpeechRecognitionAlternativeLike {
  transcript: string
}
interface SpeechRecognitionResultLike {
  isFinal: boolean
  0: SpeechRecognitionAlternativeLike
  length: number
}
interface SpeechRecognitionEventLike {
  resultIndex: number
  results: {
    length: number
    [index: number]: SpeechRecognitionResultLike
  }
}
interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function recognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function isDictationSupported(): boolean {
  return recognitionCtor() !== null && typeof isSecureContext !== "undefined" && isSecureContext
}

export function useDictation(onText: (finalText: string) => void) {
  const supported = isDictationSupported()
  const [listening, setListening] = useState(false)
  const recRef = useRef<SpeechRecognitionLike | null>(null)
  const onTextRef = useRef(onText)
  onTextRef.current = onText

  const stop = useCallback(() => {
    recRef.current?.stop()
  }, [])

  // Gesture-gated — only ever called from the Dictate button's onClick.
  const start = useCallback(() => {
    const Ctor = recognitionCtor()
    if (!Ctor || recRef.current) return
    const rec = new Ctor()
    rec.lang = "en-US"
    rec.interimResults = false
    rec.continuous = false
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i]
        if (r.isFinal && r[0]?.transcript) onTextRef.current(r[0].transcript.trim())
      }
    }
    rec.onend = () => {
      recRef.current = null
      setListening(false)
    }
    rec.onerror = () => {
      recRef.current = null
      setListening(false)
    }
    recRef.current = rec
    setListening(true)
    try {
      rec.start()
    } catch {
      recRef.current = null
      setListening(false)
    }
  }, [])

  useEffect(
    () => () => {
      recRef.current?.abort()
      recRef.current = null
    },
    [],
  )

  return { supported, listening, start, stop }
}
